// src/pages/LoginPage.js
import React, { useEffect, useState, useMemo } from "react";
import { purgeOfflineCaches } from "../serviceWorkerRegistration";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { BROCHURE, brochureGradient } from "../utils/brandPalette.generated";
import {
  Visibility,
  VisibilityOff,
  ArrowBackRounded,
  MailOutline,
  KeyRounded,
  CheckCircleRounded,
  SchoolRounded,
  SecurityRounded,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { fetchJson } from "../utils/fetchJson";
import { releaseStuckOverlays } from "../utils/uiLock";
import { useNavigate } from 'react-router-dom';
import { getHomePathForRole } from '../utils/getHomePathForRole';
import { usePlatform } from '../contexts/PlatformContext';
import { useTranslation } from 'react-i18next';
import { sessionEndReason, clearSessionEndReason, SESSION_END_REASONS } from '../utils/sessionEnd.generated';
import { AndroidAppButton } from '../components/AndroidAppDownload';
import LanguageToggle from '../components/LanguageToggle';

const API_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_API_BASE_URL) || '';

function resolveAbsoluteAssetUrl(pathOrUrl) {
  const raw = (pathOrUrl || '').toString().trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '') ||
    ''
  ).toString().trim();
  const baseClean = base.replace(/\/$/, '');
  if (!baseClean) return raw;
  const rel = raw.startsWith('/') ? raw : `/${raw}`;
  return `${baseClean}${rel}`;
}

/**
 * THE LOGIN PAGE HAS ITS OWN THEME, and does not take the school's.
 *
 * Every other screen runs inside App.js's ThemeProvider and follows whichever of
 * the four palettes the school chose. This one cannot: it renders BEFORE anyone
 * has signed in, so there is no school yet — the palette in force is whatever
 * happens to be left in this browser's localStorage from the last person to use
 * it, which is not a design decision anybody made.
 *
 * That is not academic. `MuiOutlinedInput` in themes.js paints a field
 * `p.dark ? p.tint : '#FFFFFF'`, so on Dark Mode every field on this page came
 * up #212936 — and the hover border and focus ring take the palette's brand blue
 * on all four. The page's own design is a fixed indigo-and-white brand page, and
 * the two were fighting.
 *
 * A plain light theme, declared at module scope so it is not rebuilt on every
 * render. The page's colours are the ones written into its own sx props, which
 * is exactly how it looked before any of this.
 */
const loginTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: BROCHURE.ink, dark: BROCHURE.inkDeep, contrastText: BROCHURE.onInk },
    secondary: { main: BROCHURE.gold, dark: BROCHURE.goldDeep, contrastText: BROCHURE.ink },
    background: { default: '#FFFFFF', paper: '#FFFFFF' },
    text: { primary: 'rgba(0,0,0,0.87)', secondary: 'rgba(0,0,0,0.6)' },
  },
  components: {
    MuiTextField: { defaultProps: { size: 'small' } },
    // WHITE, always. This is the override that was being lost.
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 8, backgroundColor: '#FFFFFF' } },
    },
  },
});

export default function LoginPage({ onLogin }) {
  const { platformName, platformLogoUrl, logoSize, brandingDisplay, brandingGap, platformNameFont, platformNameColor, platformNameFontSize, platformNameFontWeight } = usePlatform();
  const { t } = useTranslation();
  // Clear stale school/session context so fetchJson doesn't inject wrong headers during login
  useEffect(() => {
    try {
      ['accessToken', 'token', 'refreshToken', 'schoolId', 'activeSchoolId',
       'scopeSchoolId', 'schoolAdmin_activePhase', 'schoolAdmin_activePhaseId',
       'schoolPhases', 'currentAcademicYearId'
      ].forEach(k => localStorage.removeItem(k));
    } catch (_) {}

    // Same reason we clear the keys above: any offline-cached API responses belong
    // to the previous session's user/school and must not leak into the next one.
    try { purgeOfflineCaches(); } catch (_) {}

    // Defensive UI cleanup: an orphaned MUI modal/backdrop portal left over from the
    // previous (authenticated) view darkens the login screen and blocks all clicks.
    // Force-remove any overlay here (no modal should ever be open on the login page),
    // and run a couple of delayed passes to catch a portal that the unmounting view
    // tears down slightly after this component mounts.
    releaseStuckOverlays({ force: true });
    const t1 = setTimeout(() => releaseStuckOverlays({ force: true }), 350);
    const t2 = setTimeout(() => releaseStuckOverlays({ force: true }), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const resolvedLogo = useMemo(() => {
    if (platformLogoUrl) return resolveAbsoluteAssetUrl(platformLogoUrl);
    return '/encatarlogo.png';
  }, [platformLogoUrl]);

  const brandingNameSx = useMemo(() => ({
    ...(platformNameFont ? { fontFamily: platformNameFont } : {}),
    ...(platformNameColor ? { color: platformNameColor } : {}),
    ...(platformNameFontSize ? { fontSize: platformNameFontSize } : {}),
    ...(platformNameFontWeight ? { fontWeight: platformNameFontWeight } : {}),
  }), [platformNameColor, platformNameFont, platformNameFontSize, platformNameFontWeight]);

  const [step, setStep] = useState("credentials");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [availableSchools, setAvailableSchools] = useState([]);
  const [selectedSchoolPublicId, setSelectedSchoolPublicId] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [otp, setOtp] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // SAY WHY THEY ARE HERE, when it was not their doing.
  //
  // Arriving at this page without having signed out is indistinguishable from
  // having signed out, which is the report nobody can act on. The session
  // records its reason when it ends involuntarily; this reads it once and clears
  // it, so it explains THIS arrival and not the next.
  useEffect(() => {
    const ended = sessionEndReason();
    if (!ended) return;
    clearSessionEndReason();
    setNotice({
      [SESSION_END_REASONS.TOKEN_REJECTED]: 'Your saved sign-in was refused by the server, so it could not be restored. Signing in again will fix it.',
      [SESSION_END_REASONS.REFRESH_FAILED]: 'Your session could not be renewed — most likely the connection dropped. Please sign in again.',
      [SESSION_END_REASONS.IDLE]: 'You were signed out after a period of inactivity, as this school has configured.',
    }[ended] || 'Your session ended. Please sign in again.');
  }, []);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const navigate = useNavigate();

  const toFriendlyErrorMessage = (err) => {
    const msg = err && err.userMessage ? String(err.userMessage).trim() : '';
    if (msg) return msg;

    const raw = (err && (err.message || err.error)) ? String(err.message || err.error) : '';
    const lower = raw.toLowerCase();

    if (lower.includes('wrong password')) {
      return t('loginPage.errors.wrongPassword', { defaultValue: 'Wrong password.' });
    }
    if (lower.includes('no account found with that email')) {
      return t('loginPage.errors.noAccountForEmail', { defaultValue: 'No account was found with that email address.' });
    }
    if (lower.includes('no account found with that username')) {
      return t('loginPage.errors.noAccountForUsername', { defaultValue: 'No account was found with that username.' });
    }
    if (lower.includes('invalid credentials')) {
      return t('loginPage.errors.invalidCredentials', { defaultValue: 'The username, email, or password you entered is incorrect.' });
    }
    if (lower.includes('multiple accounts found for this username')) {
      return t('loginPage.errors.usernameUseEmail', { defaultValue: 'Multiple accounts were found for this username. Please sign in with your email address.' });
    }
    if (lower.includes('multiple accounts found')) {
      return t('loginPage.errors.multipleAccountsResolved', { defaultValue: 'Multiple accounts were found. Please select the correct school account to continue.' });
    }
    if (lower.includes('invalid or expired otp')) {
      return t('loginPage.errors.invalidOrExpiredOtp', { defaultValue: 'This verification code is invalid or has expired. Please request a new code.' });
    }
    if (lower.includes('otp expired')) {
      return t('loginPage.errors.otpExpired', { defaultValue: 'Your verification code has expired. Please request a new one.' });
    }
    if (lower.includes('invalid otp')) {
      return t('loginPage.errors.invalidOtp', { defaultValue: 'The verification code you entered is incorrect.' });
    }
    if (lower.includes('invalid or expired 2fa session')) {
      return t('loginPage.errors.invalid2faSession', { defaultValue: 'Your verification session has expired. Please sign in again to request a new code.' });
    }
    if (lower.includes('2fa code expired')) {
      return t('loginPage.errors.expired2faCode', { defaultValue: 'Your two-factor verification code has expired. Please request a new one.' });
    }
    if (lower.includes('invalid 2fa code')) {
      return t('loginPage.errors.invalid2faCode', { defaultValue: 'The two-factor verification code you entered is incorrect.' });
    }
    if (lower.includes('no account found with that email')) {
      return t('loginPage.errors.noAccountForEmail', { defaultValue: 'No account was found with that email address.' });
    }
    if (lower.includes('account has no valid email on file')) {
      return t('loginPage.errors.noValidResetEmail', { defaultValue: 'This account does not have a valid email address for password reset.' });
    }
    if (lower.includes('account is not eligible for password reset')) {
      return t('loginPage.errors.resetNotEligible', { defaultValue: 'This account is not eligible for password reset.' });
    }
    if (lower.includes('email is required')) {
      return t('loginPage.errors.emailRequired', { defaultValue: 'Please enter your email address.' });
    }
    if (lower.includes('invalid or expired token')) {
      return t('loginPage.errors.invalidResetToken', { defaultValue: 'This password reset link or code is no longer valid.' });
    }
    if (lower.includes('user not found')) {
      return t('loginPage.errors.userNotFound', { defaultValue: 'We could not find your account. Please try again.' });
    }

    // Fallbacks for cases where fetchJson isn't used / older calls still throw raw errors.
    if (lower.includes('network') || lower.includes('failed to fetch') || lower.includes('cors') || lower.includes('timeout') || lower.includes('abort')) {
      return t('common.networkError', { defaultValue: 'Network error. Please check your internet connection and try again.' });
    }
    if (lower.includes('too many') || lower.includes('rate') || lower.includes('limit')) {
      return t('loginPage.errors.tooManyRequests', { defaultValue: 'Too many attempts. Please wait a moment and try again.' });
    }

    return t('common.error', { defaultValue: 'Something went wrong. Please try again.' });
  };

  const requestOtp = async ({ schoolPublicId } = {}) => {
    const payload = {
      identifier,
      password,
      ...(schoolPublicId ? { schoolPublicId } : {}),
    };
    return fetchJson("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      skipAuth: true,
    });
  };

  // 1ï¸âƒ£ Credentials â†’ request OTP
  const handleCredentials = async () => {
    setError("");
    setNotice("");
    if (!identifier || !password) {
      return setError(t('loginPage.errors.missingIdentifierPassword'));
    }
    setLoading(true);
    try {
      const response = await requestOtp();
      if (response?.requiresSchoolSelection) {
        const schools = Array.isArray(response.schools) ? response.schools : [];
        if (!schools.length) {
          setError(t('loginPage.errors.multipleAccounts'));
          return;
        }
        setAvailableSchools(schools);
        setSelectedSchoolPublicId(schools[0]?.publicId || "");
        setStep("selectSchool");
        return;
      }
      setTempToken(response.tempToken);
      if (response.emailQueued === false) {
        setError(t('loginPage.errors.otpDeliveryIssue', { defaultValue: 'We could not start delivery of your verification code. Please contact your administrator or try again shortly.' }));
      }
      setStep("otp");
    } catch (e) {
      setError(toFriendlyErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSchool = async () => {
    setError("");
    setNotice("");
    if (!selectedSchoolPublicId) {
      return setError(t('loginPage.errors.selectSchool'));
    }
    setLoading(true);
    try {
      const response = await requestOtp({ schoolPublicId: selectedSchoolPublicId });
      if (response?.requiresSchoolSelection) {
        setError(t('loginPage.errors.selectOneSchool'));
        return;
      }
      setTempToken(response.tempToken);
      if (response.emailQueued === false) {
        setError(t('loginPage.errors.otpDeliveryIssue', { defaultValue: 'We could not start delivery of your verification code. Please contact your administrator or try again shortly.' }));
      }
      setStep("otp");
    } catch (e) {
      setError(toFriendlyErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // 2ï¸âƒ£ OTP â†’ finalize login
  const handleOtp = async () => {
    setError("");
    setNotice("");
    if (!otp) return setError(t('loginPage.errors.missingOtp'));
    setLoading(true);
    try {
      const response = await fetchJson("/api/auth/login/verify-otp", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tempToken, otp }),
        skipAuth: true,
      });
      
      const { accessToken, refreshToken, user } = response;
      localStorage.setItem("accessToken", accessToken);
      try { localStorage.setItem("token", accessToken); } catch (_) {}
      if (refreshToken) {
        try { localStorage.setItem("refreshToken", refreshToken); } catch (_) {}
      }
      
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
        // Store schoolId in localStorage for admission workflows
        if (user.schoolId) {
          localStorage.setItem("schoolId", user.schoolId);
        }
      }
      
      // Call onLogin callback
      if (onLogin) onLogin();

      try {
        window.dispatchEvent(new Event('app:auth:changed'));
      } catch (_) {}
      
      // Redirect based on role
      if (user?.role && ['SystemAdmin', 'ITManager', 'SalesRep', 'SupportAgent'].includes(user.role)) {
        navigate('/super-admin/system-overview', { replace: true });
      } else {
        const firstPath = getHomePathForRole(user?.role, user?.privileges || []);
        navigate(firstPath, { replace: true });
      }
    } catch (e) {
      setError(toFriendlyErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP functionality
  const handleResendOtp = async () => {
    setError("");
    setNotice("");
    setLoading(true);
    try {
      // Use the stored credentials from state
      if (!identifier || !password) {
        setError(t('loginPage.errors.credentialsNotFound'));
        return;
      }

      const response = await requestOtp({ schoolPublicId: selectedSchoolPublicId || undefined });
      if (response?.requiresSchoolSelection) {
        const schools = Array.isArray(response.schools) ? response.schools : [];
        if (schools.length) {
          setAvailableSchools(schools);
          setSelectedSchoolPublicId(schools[0]?.publicId || "");
          setStep("selectSchool");
          return;
        }
        setError(t('loginPage.errors.multipleAccounts'));
        return;
      }

      setTempToken(response.tempToken);
      setOtpCooldown(30); // 30 second cooldown
      
      // Start cooldown timer
      const timer = setInterval(() => {
        setOtpCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (e) {
      setError(toFriendlyErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ðŸ”‘ Forgot Password â†’ send reset code
  const handleForgot = async () => {
    setError("");
    setNotice("");
    if (!resetEmail) return setError(t('loginPage.errors.missingEmail'));
    setLoading(true);
    try {
      await fetchJson("/api/auth/forgot-password", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
        skipAuth: true,
      });
      setStep("reset");
    } catch (e) {
      setError(toFriendlyErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ðŸ”„ Reset Password â†’ apply new password
  const handleReset = async () => {
    setError("");
    setNotice("");
    if (!resetCode || !newPassword) {
      return setError(t('loginPage.errors.resetMissingCodePassword'));
    }
    if (newPassword.length < 7) {
      return setError(t('loginPage.errors.passwordTooShort') || 'Password must be at least 7 characters');
    }
    if (!/[a-zA-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      return setError(t('loginPage.errors.passwordWeak') || 'Password must contain at least one letter and one number');
    }
    setLoading(true);
    try {
      const response = await fetchJson("/api/auth/reset-password", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail, code: resetCode, newPassword }),
        skipAuth: true,
      });

      // The reset now returns a session. Sending the user back to sign in made
      // them prove themselves twice: the emailed reset code already established
      // control of the mailbox, which is exactly what the login OTP checks.
      if (response?.accessToken) {
        localStorage.setItem("accessToken", response.accessToken);
        try { localStorage.setItem("token", response.accessToken); } catch (_) {}
        if (response.refreshToken) {
          try { localStorage.setItem("refreshToken", response.refreshToken); } catch (_) {}
        }
        const user = response.user;
        if (user) {
          localStorage.setItem("user", JSON.stringify(user));
          if (user.schoolId) localStorage.setItem("schoolId", user.schoolId);
        }
        if (onLogin) onLogin();
        try { window.dispatchEvent(new Event('app:auth:changed')); } catch (_) {}

        if (user?.role && ['SystemAdmin', 'ITManager', 'SalesRep', 'SupportAgent'].includes(user.role)) {
          navigate('/super-admin/system-overview', { replace: true });
        } else {
          navigate(getHomePathForRole(user?.role, user?.privileges || []), { replace: true });
        }
        return;
      }

      // Password changed but no session issued — fall back to signing in, and
      // say so, otherwise the user retries with the old password.
      setStep("credentials");
      setIdentifier(resetEmail);
      setPassword("");
      setNotice(t('loginPage.messages.passwordResetSignIn'));
    } catch (e) {
      setError(toFriendlyErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={loginTheme}>
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* LEFT BRANDING PANEL */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        width: '44%',
        minHeight: '100vh',
        background: brochureGradient(145),
        position: 'relative',
        overflow: 'hidden',
        color: 'white',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage: 'url("https://img.freepik.com/free-vector/hand-drawn-back-school-background_23-2149464866.jpg")',
          backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.07,
        }} />
        {[
          { top: -100, right: -100, size: 360, opacity: 0.07, filled: false },
          { top: -50, right: -50, size: 220, opacity: 0.1, filled: false },
          { bottom: -80, left: -80, size: 300, opacity: 0.06, filled: false },
          { bottom: 120, right: 30, size: 140, opacity: 0.1, filled: true },
        ].map((c, i) => (
          <Box key={i} sx={{
            position: 'absolute',
            ...(c.top !== undefined ? { top: c.top } : {}),
            ...(c.bottom !== undefined ? { bottom: c.bottom } : {}),
            ...(c.left !== undefined ? { left: c.left } : {}),
            ...(c.right !== undefined ? { right: c.right } : {}),
            width: c.size, height: c.size, borderRadius: '50%',
            ...(c.filled
              ? { background: `rgba(3,169,244,${c.opacity})` }
              : { border: `2px solid rgba(255,255,255,${c.opacity})` }),
            zIndex: 0,
          }} />
        ))}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', p: { md: 5, lg: 6 } }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start',
            bgcolor: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 3, px: 2.5, py: 1.25, mb: 'auto',
          }}>
            {resolvedLogo && brandingDisplay !== 'nameOnly' && (
              <Box component="img" src={resolvedLogo} alt="Logo"
                sx={{ height: 34, objectFit: 'contain', mr: brandingDisplay === 'logoOnly' ? 0 : 1.5 }}
              />
            )}
            {brandingDisplay !== 'logoOnly' && (
              <Typography fontWeight={800} sx={{ color: 'white', fontSize: '1.05rem', letterSpacing: 0.2, ...brandingNameSx }}>
                {platformName || 'Encatar'}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <Typography variant="h3" fontWeight={800} sx={{ mb: 2.5, lineHeight: 1.2, fontSize: { md: '2rem', lg: '2.4rem' } }}>
                The Future of School Management
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, opacity: 0.85, lineHeight: 1.75, fontSize: '0.97rem' }}>
                {t('loginPage.subtitle', 'School Management Platform')} — built to connect your entire school in one secure, modern system.
              </Typography>
              {['Secure two-factor authentication', 'Role-based access for all users', 'Multi-school & multi-phase support', 'Real-time data and analytics'].map((feat, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(3,169,244,0.25)', border: '1px solid rgba(3,169,244,0.55)',
                    }}>
                      <CheckCircleRounded sx={{ fontSize: 13, color: '#1F5FBF' }} />
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>{feat}</Typography>
                  </Stack>
                </motion.div>
              ))}
            </motion.div>
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.45, letterSpacing: 0.4 }}>
            {t('loginPage.footer', 'Empowering Education Through Technology')}
          </Typography>
        </Box>
      </Box>
      {/* RIGHT FORM PANEL */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#ffffff' }}>
        {/* Top nav bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, sm: 4 }, py: 1.75,
          borderBottom: '1px solid #DDDAEF',
        }}>
          <Button
            component="a" href="/"
            startIcon={<ArrowBackRounded sx={{ fontSize: '1rem !important' }} />}
            sx={{ color: '#241C6B', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }}
          >
            Back to Home
          </Button>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            {resolvedLogo && (
              <Box component="img" src={resolvedLogo} alt="Logo" sx={{ height: 28, objectFit: 'contain' }} />
            )}
            {brandingDisplay !== 'logoOnly' && (
              <Typography fontWeight={700} sx={{ color: '#241C6B', fontSize: '0.9rem', ...brandingNameSx }}>
                {platformName || 'Encatar'}
              </Typography>
            )}
          </Box>
          {/* THE LANGUAGE CHOICE AND THE RE-ASSURANCE, in that order. The right
              end of the bar is where the landing page keeps its toggle, so a
              visitor who came through the front door finds it in the same
              corner — and one who came straight to /login finds it at all. */}
          <Stack direction="row" spacing={1.25} alignItems="center">
            <LanguageToggle />
            <Chip
              icon={<SecurityRounded />}
              label="Secured"
              size="small"
              sx={{ bgcolor: '#DDDAEF', color: '#241C6B', fontWeight: 700, fontSize: '0.7rem', '& .MuiChip-icon': { color: '#1F5FBF', fontSize: '14px' } }}
            />
          </Stack>
        </Box>

        {/* Form area */}
        <Box sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          px: { xs: 2, sm: 4 }, py: { xs: 3, sm: 5 },
          overflowY: 'auto',
        }}>
          <Box sx={{ width: '100%', maxWidth: 420 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`hdr-${step}`}
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{ duration: 0.2 }}
              >
                <Box sx={{ mb: 3 }}>
                  {/* THE LOGO WHERE THE PADLOCK WAS.
                      Drawn plainly rather than dropped into the gradient tile:
                      the mark's deepest petal is navy and the tile runs indigo
                      to sky, so it would have all but disappeared into it. The
                      school's own logo when one is configured, the Encatar mark
                      otherwise — resolvedLogo already answers that question for
                      the header above.

                      The OTHER steps keep their icon, because a mail or a key
                      says what is happening on that step and a logo would say
                      nothing. */}
                  {step === 'credentials' ? (
                    <Box
                      component="img"
                      src={resolvedLogo}
                      alt={platformName || 'Encatar'}
                      sx={{
                        height: 50,
                        // A school can upload a wide banner rather than a square
                        // mark, so the width is capped instead of left to the
                        // file's own proportions.
                        maxWidth: 180,
                        objectFit: 'contain',
                        objectPosition: 'left center',
                        display: 'block',
                        mb: 2,
                      }}
                    />
                  ) : (
                    <Box sx={{
                      width: 50, height: 50, borderRadius: '14px',
                      background: 'linear-gradient(135deg, #241C6B 0%, #1F5FBF 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                      boxShadow: '0 8px 20px rgba(26,35,126,0.25)',
                    }}>
                      {step === 'otp' && <MailOutline sx={{ color: 'white', fontSize: 22 }} />}
                      {(step === 'forgot' || step === 'reset') && <KeyRounded sx={{ color: 'white', fontSize: 22 }} />}
                      {step === 'selectSchool' && <SchoolRounded sx={{ color: 'white', fontSize: 22 }} />}
                    </Box>
                  )}
                  <Typography variant="h5" fontWeight={800} sx={{ color: '#241C6B', mb: 0.5 }}>
                    {step === 'credentials' && 'Welcome Back'}
                    {step === 'selectSchool' && 'Select Your School'}
                    {step === 'otp' && 'Verify Your Identity'}
                    {step === 'forgot' && 'Reset Password'}
                    {step === 'reset' && 'Set New Password'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {step === 'credentials' && 'Sign in to your account to continue.'}
                    {step === 'selectSchool' && 'Multiple accounts found — choose the school to access.'}
                    {step === 'otp' && 'Enter the 6-digit code sent to your email address.'}
                    {step === 'forgot' && "Enter your email and we'll send you a reset code."}
                    {step === 'reset' && 'Enter the reset code and choose a new password.'}
                  </Typography>
                </Box>
              </motion.div>
            </AnimatePresence>

            {['credentials', 'otp', 'selectSchool'].includes(step) && (
              <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                {[0, 1].map((idx) => {
                  const done = idx === 0 && (step === 'otp' || step === 'selectSchool');
                  const active = (idx === 0 && step === 'credentials') || (idx === 1 && (step === 'otp' || step === 'selectSchool'));
                  return (
                    <Box key={idx} sx={{
                      height: 4, flex: 1, borderRadius: 2,
                      bgcolor: done ? '#1F5FBF' : active ? '#241C6B' : '#DDDAEF',
                      transition: 'background-color 0.35s ease',
                    }} />
                  );
                })}
              </Stack>
            )}

            {notice && <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>{notice}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -28 }}
                transition={{ duration: 0.22, ease: 'easeInOut' }}
              >
                {/* A REAL FORM, which is what "remember me" means on the web.
                    The phone keeps credentials itself, in the hardware keystore,
                    and now holds one per school with a chooser. The web must NOT
                    copy that: a password in localStorage is readable by any
                    script that ever gets injected, and the browser already has a
                    password manager that does the same job properly — OS
                    keychain, one entry per account, a chooser when there is more
                    than one, and a fill that works across the whole site.

                    It just never got the chance. Password managers key their
                    "save this?" prompt on a FORM SUBMISSION, and this was a Box
                    with Enter-key handlers on two fields — so nothing was ever
                    submitted and nothing was ever offered. Same feature, same
                    behaviour, done the way the platform does it. */}
                {step === 'credentials' && (
                  <Box component="form" onSubmit={(e) => { e.preventDefault(); handleCredentials(); }}>
                    <TextField
                      label={t('loginPage.fields.identifier')}
                      fullWidth sx={{ mb: 2 }}
                      value={identifier} onChange={e => setIdentifier(e.target.value)}
                      disabled={loading}
                      autoComplete="username"
                    />
                    <TextField
                      label={t('loginPage.fields.password')}
                      type={showPwd ? 'text' : 'password'}
                      fullWidth sx={{ mb: 0.5 }}
                      value={password} onChange={e => setPassword(e.target.value)}
                      disabled={loading}
                      autoComplete="current-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPwd(p => !p)} edge="end" size="small">
                              {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                      <Button
                        variant="text" size="small"
                        onClick={() => setStep('forgot')}
                        disabled={loading}
                        sx={{ textTransform: 'none', color: '#241C6B', fontWeight: 600, p: 0, minWidth: 0 }}
                      >
                        {t('loginPage.buttons.forgotPassword')}
                      </Button>
                    </Box>
                    <Button
                      // submit, not onClick — a click handler bypasses the form
                      // and the password manager never sees a submission, which
                      // is the whole reason it never offered to save anything.
                      type="submit"
                      variant="contained" fullWidth size="large"
                      disabled={loading}
                      sx={{
                        background: 'linear-gradient(90deg, #241C6B 0%, #1B3A8F 100%)',
                        color: '#FFFFFF',
                        '&:hover': { background: 'linear-gradient(90deg, #1A1450 0%, #1F5FBF 100%)' },
                        fontWeight: 700, borderRadius: 2, py: 1.45, fontSize: '1rem',
                        boxShadow: '0 4px 16px rgba(26,35,126,0.22)',
                      }}
                    >
                      {loading ? <CircularProgress size={22} color="inherit" /> : t('loginPage.buttons.signIn')}
                    </Button>
                  </Box>
                )}

                {step === 'selectSchool' && (
                  <Box>
                    <TextField
                      select label={t('loginPage.fields.school')}
                      fullWidth sx={{ mb: 3 }}
                      value={selectedSchoolPublicId}
                      onChange={(e) => setSelectedSchoolPublicId(e.target.value)}
                      disabled={loading}
                    >
                      {availableSchools.map((s) => (
                        <MenuItem key={s.publicId || s.code || s.name} value={s.publicId}>
                          {s.name}{s.code ? ` (${s.code})` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Stack spacing={2}>
                      <Button
                        variant="contained" fullWidth size="large"
                        onClick={handleSelectSchool}
                        disabled={loading || !selectedSchoolPublicId}
                        sx={{
                          background: 'linear-gradient(90deg, #241C6B 0%, #1B3A8F 100%)',
                          color: '#FFFFFF',
                          '&:hover': { background: 'linear-gradient(90deg, #1A1450 0%, #1F5FBF 100%)' },
                          fontWeight: 700, borderRadius: 2, py: 1.45,
                          boxShadow: '0 4px 16px rgba(26,35,126,0.22)',
                        }}
                      >
                        {loading ? <CircularProgress size={22} color="inherit" /> : t('loginPage.buttons.continue')}
                      </Button>
                      <Button
                        variant="outlined" fullWidth
                        onClick={() => setStep('credentials')} disabled={loading}
                        sx={{ borderColor: '#241C6B', color: '#241C6B', fontWeight: 600, borderRadius: 2 }}
                      >
                        {t('loginPage.buttons.back')}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {step === 'otp' && (
                  <Box component="form" onSubmit={e => { e.preventDefault(); handleOtp(); }}>
                    <TextField
                      label={t('loginPage.fields.otp')}
                      fullWidth sx={{ mb: 3 }}
                      value={otp} onChange={e => setOtp(e.target.value)}
                      disabled={loading}
                      inputProps={{ maxLength: 6, style: { letterSpacing: '0.35em', fontWeight: 700, fontSize: '1.3rem', textAlign: 'center' } }}
                      placeholder="· · · · · ·"
                    />
                    <Stack spacing={2}>
                      <Button
                        type="submit" variant="contained" fullWidth size="large"
                        disabled={loading || !otp}
                        sx={{
                          background: 'linear-gradient(90deg, #241C6B 0%, #1B3A8F 100%)',
                          color: '#FFFFFF',
                          '&:hover': { background: 'linear-gradient(90deg, #1A1450 0%, #1F5FBF 100%)' },
                          fontWeight: 700, borderRadius: 2, py: 1.45,
                          boxShadow: '0 4px 16px rgba(26,35,126,0.22)',
                        }}
                      >
                        {loading ? <CircularProgress size={22} color="inherit" /> : t('loginPage.buttons.verifyOtp')}
                      </Button>
                      <Button
                        variant="outlined" fullWidth
                        onClick={handleResendOtp}
                        disabled={loading || otpCooldown > 0}
                        sx={{ borderColor: '#241C6B', color: '#241C6B', fontWeight: 600, borderRadius: 2 }}
                      >
                        {otpCooldown > 0
                          ? t('loginPage.buttons.resendIn', { seconds: otpCooldown })
                          : t('loginPage.buttons.resendOtp')}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {step === 'forgot' && (
                  <Box>
                    <TextField
                      label={t('loginPage.fields.email')}
                      type="email" fullWidth sx={{ mb: 3 }}
                      value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                      disabled={loading}
                      onKeyDown={e => e.key === 'Enter' && handleForgot()}
                      autoComplete="email"
                    />
                    <Stack spacing={2}>
                      <Button
                        variant="contained" fullWidth size="large"
                        onClick={handleForgot} disabled={loading}
                        sx={{
                          background: 'linear-gradient(90deg, #241C6B 0%, #1B3A8F 100%)',
                          color: '#FFFFFF',
                          '&:hover': { background: 'linear-gradient(90deg, #1A1450 0%, #1F5FBF 100%)' },
                          fontWeight: 700, borderRadius: 2, py: 1.45,
                          boxShadow: '0 4px 16px rgba(26,35,126,0.22)',
                        }}
                      >
                        {loading ? <CircularProgress size={22} color="inherit" /> : t('loginPage.buttons.sendResetCode')}
                      </Button>
                      <Button
                        variant="outlined" fullWidth
                        onClick={() => setStep('credentials')} disabled={loading}
                        sx={{ borderColor: '#241C6B', color: '#241C6B', fontWeight: 600, borderRadius: 2 }}
                      >
                        {t('loginPage.buttons.back')}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {step === 'reset' && (
                  <Box>
                    <TextField
                      label={t('loginPage.fields.resetCode')}
                      fullWidth sx={{ mb: 2 }}
                      value={resetCode} onChange={e => setResetCode(e.target.value)}
                      disabled={loading}
                      inputProps={{ style: { letterSpacing: '0.2em', fontWeight: 600 } }}
                    />
                    <TextField
                      label={t('loginPage.fields.newPassword')}
                      type={showPwd ? 'text' : 'password'}
                      fullWidth sx={{ mb: 3 }}
                      value={newPassword} onChange={e => setNewPassword(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPwd(p => !p)} edge="end" size="small">
                              {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="contained" fullWidth size="large"
                      onClick={handleReset} disabled={loading}
                      sx={{
                        background: 'linear-gradient(90deg, #241C6B 0%, #1B3A8F 100%)',
                        color: '#FFFFFF',
                        '&:hover': { background: 'linear-gradient(90deg, #1A1450 0%, #1F5FBF 100%)' },
                        fontWeight: 700, borderRadius: 2, py: 1.45,
                        boxShadow: '0 4px 16px rgba(26,35,126,0.22)',
                      }}
                    >
                      {loading ? <CircularProgress size={22} color="inherit" /> : t('loginPage.buttons.resetPassword')}
                    </Button>
                  </Box>
                )}

              </motion.div>
            </AnimatePresence>

            <Divider sx={{ my: 3 }} />
            {/* EVERY USER PASSES THROUGH HERE. A teacher signing in on a phone
                browser is one tap from the app that does the same job better
                offline, and this is the only page they all have in common. */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              <AndroidAppButton
                variant="nav"
                size="small"
                sx={{ borderColor: '#DDDAEF', color: '#241C6B', fontWeight: 600, borderRadius: 2 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', lineHeight: 1.6 }}>
              Protected by enterprise-grade security & two-factor authentication.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 4 }, py: 1.75, borderTop: '1px solid #DDDAEF', textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'rgba(26,35,126,0.45)', letterSpacing: 0.4, fontWeight: 500 }}>
            {t('loginPage.footer', 'Empowering Education Through Technology')}
          </Typography>
        </Box>
      </Box>
    </Box>
    </ThemeProvider>
  );
}
